import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Area,
  AreaChart,
} from "recharts";
import { ArrowLeft } from "lucide-react";
import { fetchTrader } from "@/lib/api";
import { formatUSD, formatPct, formatTime } from "@/lib/format";
import { motion } from "framer-motion";

const formatExpiry = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y.slice(2)}`;
};

const ACTION_LABELS = {
  buy: "BUY",
  sell: "SELL",
  hold: "HOLD",
  buy_call: "BUY CALL",
  buy_put: "BUY PUT",
  close_call: "CLOSE CALL",
  close_put: "CLOSE PUT",
  expire_call: "CALL EXPIRY",
  expire_put: "PUT EXPIRY",
};

export default function TraderDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const d = await fetchTrader(id);
        if (active) setData(d);
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 15000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [id]);

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-arena-soft">
        Loading trader…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 font-mono text-arena-soft">
        Trader not found.
        <Link to="/" className="underline text-arena-ink">← Back to arena</Link>
      </div>
    );
  }

  const positive = data.pnl >= 0;
  const m = data.metrics || {};
  const chartData = (data.snapshots || []).map((s) => ({
    ts: s.timestamp,
    label: new Date(s.timestamp).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit" }),
    total: s.total_value,
    cash: s.cash,
    stocks: s.holdings_value,
    options: s.options_value,
  }));

  const trades = data.trades || [];
  const executedTrades = trades.filter((t) => t.executed);
  const winningTrades = executedTrades.filter((t) => (t.action === "sell" || t.action.startsWith("close_") || t.action.startsWith("expire_")) && t.total > 0);

  const holdings = Object.entries(data.holdings || {}).filter(([, q]) => q > 0);
  const options = (data.options || []).filter((o) => o.contracts > 0);

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-arena-line">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-5 flex items-center gap-6 flex-wrap">
          <Link
            to="/"
            className="flex items-center gap-2 text-arena-ink hover:text-arena-soft transition-colors no-underline text-sm font-mono uppercase tracking-[0.2em]"
            data-testid="back-to-arena"
          >
            <ArrowLeft size={16} /> Back to Arena
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <div className="w-2 h-2" style={{ background: data.color }} />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-arena-soft">
              {data.provider} · {data.model}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 lg:px-10 py-8 space-y-px bg-arena-line">
        {/* Hero */}
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 lg:p-12 border border-arena-line relative overflow-hidden"
          data-testid="trader-hero"
        >
          <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: data.color }} />
          <div className="flex items-start gap-6 flex-wrap">
            <div
              className="w-20 h-20 flex items-center justify-center font-display font-black text-white text-3xl"
              style={{ background: data.color }}
            >
              {data.name.split(" ")[0].slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-black text-5xl lg:text-6xl tracking-tighter">
                {data.name}
              </div>
              <div className="text-sm text-arena-soft mt-2 font-ui">{data.tagline}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
                Portfolio
              </div>
              <div className="font-mono font-bold text-5xl tabular-nums">
                {formatUSD(data.total_value)}
              </div>
              <div className={`font-mono text-sm mt-1 tabular-nums ${positive ? "text-profit" : "text-loss"}`}>
                {formatUSD(data.pnl, { sign: true })} ({formatPct(data.pnl_pct)})
              </div>
            </div>
          </div>
        </motion.section>

        {/* Metrics row */}
        <section className="grid grid-cols-2 md:grid-cols-6 arena-grid">
          {[
            { label: "Cash", value: formatUSD(data.cash) },
            { label: "Stocks Value", value: formatUSD(data.holdings_value) },
            { label: "Options Value", value: formatUSD(data.options_value || 0) },
            { label: "Sharpe", value: (m.sharpe ?? 0).toFixed(3) },
            { label: "Max Drawdown", value: `${(m.max_drawdown_pct ?? 0).toFixed(2)}%` },
            { label: "Volatility", value: `${(m.volatility_pct ?? 0).toFixed(2)}%` },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white p-5 lg:p-6">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
                {kpi.label}
              </div>
              <div className="font-mono font-bold text-lg lg:text-xl tabular-nums mt-1">
                {kpi.value}
              </div>
            </div>
          ))}
        </section>

        {/* Equity area chart */}
        <section className="bg-white p-6 lg:p-8 border border-arena-line">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display font-black text-2xl tracking-tighter">EQUITY DECOMPOSITION</h2>
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
              Cash · Stocks · Options
            </span>
          </div>
          <div style={{ width: "100%", height: 360 }}>
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center font-mono text-arena-soft text-sm">
                Awaiting snapshots…
              </div>
            ) : (
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#E5E5E5" />
                  <XAxis dataKey="label" tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: "#737373" }} axisLine={{ stroke: "#E5E5E5" }} tickLine={false} />
                  <YAxis tick={{ fontFamily: "IBM Plex Mono", fontSize: 10, fill: "#737373" }} axisLine={{ stroke: "#E5E5E5" }} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "1px solid #111", borderRadius: 0, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                    formatter={(v) => `$${Number(v).toFixed(2)}`}
                  />
                  <ReferenceLine y={10000} stroke="#A3A3A3" strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="cash" stackId="1" stroke="#A3A3A3" fill="#A3A3A3" fillOpacity={0.4} />
                  <Area type="monotone" dataKey="stocks" stackId="1" stroke={data.color} fill={data.color} fillOpacity={0.35} />
                  <Area type="monotone" dataKey="options" stackId="1" stroke={data.color} fill={data.color} fillOpacity={0.7} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        {/* Positions */}
        <section className="grid grid-cols-1 lg:grid-cols-2 arena-grid">
          <div className="bg-white p-6 lg:p-8">
            <h3 className="font-display font-black text-lg tracking-tight mb-3">
              STOCK POSITIONS ({holdings.length})
            </h3>
            {holdings.length === 0 ? (
              <div className="text-sm text-arena-soft font-mono">— no stock positions</div>
            ) : (
              <table className="w-full text-sm font-mono tabular-nums">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft border-b border-arena-line">
                    <th className="py-2">Ticker</th>
                    <th className="py-2 text-right">Shares</th>
                  </tr>
                </thead>
                <tbody>
                  {holdings.map(([sym, qty]) => (
                    <tr key={sym} className="border-b border-arena-line/60">
                      <td className="py-2 font-bold">{sym}</td>
                      <td className="py-2 text-right">{qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="bg-white p-6 lg:p-8">
            <h3 className="font-display font-black text-lg tracking-tight mb-3">
              OPTION POSITIONS ({options.length})
            </h3>
            {options.length === 0 ? (
              <div className="text-sm text-arena-soft font-mono">— no open options</div>
            ) : (
              <table className="w-full text-xs font-mono tabular-nums">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft border-b border-arena-line">
                    <th className="py-2">Type</th>
                    <th className="py-2">Underlying</th>
                    <th className="py-2 text-right">Strike</th>
                    <th className="py-2 text-right">Expiry</th>
                    <th className="py-2 text-right">Contracts</th>
                    <th className="py-2 text-right">Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {options.map((o) => (
                    <tr key={o.id} className="border-b border-arena-line/60">
                      <td className="py-2">
                        <span className={`px-1.5 py-[1px] text-[9px] font-bold tracking-widest text-white ${o.type === "call" ? "bg-profit" : "bg-loss"}`}>
                          {o.type.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-2 font-bold">{o.underlying}</td>
                      <td className="py-2 text-right">${o.strike.toFixed(2)}</td>
                      <td className="py-2 text-right">{formatExpiry(o.expiry)}</td>
                      <td className="py-2 text-right">{o.contracts}</td>
                      <td className="py-2 text-right">${o.cost_basis.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Trade history */}
        <section className="bg-white p-6 lg:p-8 border border-arena-line">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display font-black text-2xl tracking-tighter">TRADE HISTORY</h2>
            <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
              {executedTrades.length} executed · {trades.length - executedTrades.length} held/rejected
            </span>
          </div>
          <div className="space-y-px bg-arena-line">
            {trades.length === 0 ? (
              <div className="bg-white p-6 text-center text-arena-soft text-sm font-mono">
                No trades yet.
              </div>
            ) : (
              trades.map((t) => {
                const isOpt = t.action.startsWith("buy_") || t.action.startsWith("close_") || t.action.startsWith("expire_");
                return (
                  <div key={t.id} className="bg-white px-4 py-3" data-testid={`trade-row-${t.id}`}>
                    <div className="flex items-center gap-3 flex-wrap text-sm font-mono tabular-nums">
                      <span className={`px-2 py-[2px] text-[10px] font-bold tracking-[0.2em] ${t.executed ? "bg-arena-ink text-white" : "border border-arena-line text-arena-soft"}`}>
                        {ACTION_LABELS[t.action] || t.action.toUpperCase()}
                      </span>
                      {t.symbol && t.action !== "hold" && (
                        <span className="font-bold">
                          {t.qty}{isOpt ? "c" : ""} × {t.symbol}
                          {isOpt && t.option_meta && (
                            <span className="text-arena-soft ml-1">
                              ${t.option_meta.strike?.toFixed?.(0) || t.option_meta.strike} {formatExpiry(t.option_meta.expiry)}
                            </span>
                          )}
                        </span>
                      )}
                      {t.executed && t.action !== "hold" && (
                        <span className="text-arena-soft">@ {formatUSD(t.price)} = {formatUSD(t.total)}</span>
                      )}
                      <span className="ml-auto text-[10px] uppercase tracking-[0.2em] text-arena-soft">
                        {formatTime(t.timestamp)}
                      </span>
                    </div>
                    {t.reasoning && (
                      <blockquote className="mt-2 pl-3 border-l-2 text-xs italic text-arena-ink font-ui" style={{ borderColor: data.color }}>
                        "{t.reasoning}"
                      </blockquote>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>

      <footer className="border-t border-arena-line py-6 text-center text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
        AI Trading Arena · Built on Emergent
      </footer>
    </div>
  );
}
